import React, { type Ref, useEffect, useRef, useState } from 'react';
import { useTranslation } from '@/app/components/translations/Translation';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useCuriosityManager } from '@/app/common/CuriosityManagerProvider.tsx';
import useOnScreen from './useOnScreen';
import { analyticsUserInteraction } from '../../Analytics/analyticsUserInteraction';
import { Actions, Categories, InteractionLabel } from '../../Analytics/utils';
import { ShowMoreSeparator, ShowMoreSpinner, ShowMoreStyled } from './styled';
import { sumClasses } from '../../utils/misc';

export type ShowMoreProps = {
  handleOnClick: (setAutoLoad: React.Dispatch<React.SetStateAction<boolean>> | undefined) => void;
  showMore?: boolean;
};

const ShowMore = ({ handleOnClick, showMore }: ShowMoreProps) => {
  const ref = useRef(null);
  const { isVisible } = useOnScreen({ ref });
  const [count, setCount] = useState(0);
  const [autoLoad, setAutoLoad] = useState(() => true);
  const limit = 5;
  const hasInfiniteScroll = isVisible && autoLoad && count < limit;
  const { searchParams, searchInfoDetailData } = usePageWrapperData();
  const { t } = useTranslation();
  const { curiosityClickEvents } = useCuriosityManager();

  useEffect(() => {
    if (hasInfiniteScroll) {
      curiosityClickEvents.indexResultsShowMore('auto');
      setCount((prevState) => prevState + 1);
      handleOnClick(() => true);
    }
  }, [isVisible]);

  const handleClick = () => {
    curiosityClickEvents.indexResultsShowMore('');

    analyticsUserInteraction({
      searchParams,
      category: Categories.cta,
      action: Actions.showMore,
      label: InteractionLabel.click,
      searchInfoDetailData,
    });

    handleOnClick(setAutoLoad);
  };

  return (
    <>
      <ShowMoreSeparator />
      <ShowMoreStyled
        className={sumClasses(['openx-ui-discover', 'openx-ui-discover-collapsed'])}
        onClick={handleClick}
        ref={ref as Ref<HTMLDivElement> | undefined}
      >
        <ShowMoreSpinner className="openx-ui-discover-spinner" />
        <div className="openx-ui-discover-text">
          {showMore ? t('vacation.oss.cards.view_more') : t('vacation.oss.listings.discovermore')}
        </div>
      </ShowMoreStyled>
    </>
  );
};

export default ShowMore;
